import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, ConfigProvider, Flex, Segmented } from "antd";
import {
  getPackageAnalyticsExpired,
  getPackageAnalyticsLowCredits,
} from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { colors } from "shared/theme/color";
import {
  AnalyticsRange,
  CustomDayRange,
  ExpiringSoonThresholdDays,
  PackageExpiryRow,
  PackageType,
} from "shared/types";
import PATHS from "shared/constants/paths";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { useTabParam } from "hooks/useTabParam";
import {
  analyticsRangeStamp,
  creditThresholdStamp,
  thresholdStamp,
} from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { tzDate } from "shared/utils/dayjs";
import PackageExpiryTable from "./PackageExpiryTable";
import InlineDaysInput from "./InlineDaysInput";
import RangeSegmented, { pillSegmentedTheme } from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ExpiryWhich, expirySpec, lowCreditsSpec } from "pdf/reports/specs/archetype3";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

const EXPIRING_SOON_DAYS_MIN = 1;
const EXPIRING_SOON_DAYS_MAX = 90;
// Mirrors the server's fallback (EXPIRING_SOON_DAYS_FALLBACK in
// services/analytics/package.ts) and the notify_package_expiry_days default.
const EXPIRING_SOON_FALLBACK = 7;
// Default mirrors the low_credit notification's default threshold.
const LOW_CREDIT_MIN = 1;
const LOW_CREDIT_MAX = 50;
// Mirrors the server's fallback (LOW_CREDIT_THRESHOLD_FALLBACK in
// services/analytics/package.ts) and the notify_low_credit_threshold default.
const LOW_CREDIT_FALLBACK = 2;

type PackageStatusView = "expiring_soon" | "low_credits" | "expired" | "unactivated";

// "Akan habis" is paket-only: a membership has no credit pool to run down
// (the server compute is paket-only by construction too). "Belum aktif" is
// the first-use packages whose clock has not started; the server keeps them
// out of the other views.
const PAKET_VIEWS = ["expiring_soon", "low_credits", "expired", "unactivated"] as const;
const MEMBERSHIP_VIEWS = ["expiring_soon", "expired", "unactivated"] as const;

// The export endpoint is slug-addressed (the expired and expiring-soon views
// share one JSON view), so the slug follows the active view, never the page.
const EXPIRY_SLUGS = {
  paket: {
    expired: "kredit-kedaluwarsa",
    expiring_soon: "kredit-akan-kedaluwarsa",
    unactivated: "kredit-belum-aktif",
  },
  membership: {
    expired: "membership-kedaluwarsa",
    expiring_soon: "membership-akan-kedaluwarsa",
    unactivated: "membership-belum-aktif",
  },
} as const;

interface PackageExpiryReportContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

// Combined status report — one page per type (Paket / Membership) whose
// Segmented switches between the expiring-soon / low-credits / expired views
// (?view=). The type stays fixed per route; only the view switches in-page.
const PackageExpiryReportContent = ({
  type,
  title,
  backTo,
}: PackageExpiryReportContentProps) => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  const namespace = type === "membership" ? "membership" : "package";
  // The settings query is shared and cached; the Belum Aktif description
  // points to Masa Berlaku Produk instead of quoting the grace period
  // (chris, 2026-10-17: no number in the sentence).
  const { data: businessSettings, isLoading: settingsLoading } = useBusinessSettings();

  const views: readonly PackageStatusView[] =
    type === "paket" ? PAKET_VIEWS : MEMBERSHIP_VIEWS;
  const { activeKey, onChange } = useTabParam("expiring_soon", "view", views);
  const view = activeKey as PackageStatusView;
  const handleViewChange = (key: string) => {
    // Reset the lifted sort so a PDF exported after switching can't caption
    // the previous view's sort (the table itself remounts via key={view}).
    onSortChange(null);
    onChange(key);
  };

  // Per-view controls live up here so switching views never loses them.
  // Only "expiring soon" has a days threshold to tune — it's a forward-looking
  // rule, not a lookback window. "Recently expired" is a plain rolling
  // window instead (same as every other range-based report), via `range`.
  // Akan Kedaluwarsa opens on the studio's OWN package-expiry alert window
  // while that alert is on, else the built-in 7, the same rule as Kredit
  // Akan Habis below (and as the product page and Performa): the report and
  // the nightly alert then list the same packages. The admin's own number
  // overrides it for the visit.
  const settingsExpiryDefault = businessSettings
    ? businessSettings.notify_package_expiry
      ? businessSettings.notify_package_expiry_days
      : EXPIRING_SOON_FALLBACK
    : undefined;
  const [expiryOverride, setExpiryOverride] =
    useState<ExpiringSoonThresholdDays | null>(null);
  const threshold =
    expiryOverride ?? settingsExpiryDefault ?? EXPIRING_SOON_FALLBACK;
  const setThreshold = (next: ExpiringSoonThresholdDays) => setExpiryOverride(next);
  const expiryReady = expiryOverride !== null || !settingsLoading;
  // Kredit Akan Habis opens on the studio's OWN low-credit alert threshold
  // while that alert is on, else the built-in 2 (System Review S12, Andrew's
  // call): the report and the nightly alert then list the same packages
  // without the admin re-typing the number. The control keeps whatever the
  // admin sets for the visit (the override); the settings-derived default is
  // only the starting value, so a later settings edit never moves a number
  // the admin already changed here.
  const settingsLowCreditDefault = businessSettings
    ? businessSettings.notify_low_credit
      ? businessSettings.notify_low_credit_threshold
      : LOW_CREDIT_FALLBACK
    : undefined;
  const [lowCreditOverride, setLowCreditOverride] = useState<number | null>(null);
  const lowCreditThreshold =
    lowCreditOverride ?? settingsLowCreditDefault ?? LOW_CREDIT_FALLBACK;
  const setLowCreditThreshold = (next: number) => setLowCreditOverride(next);
  // Wait for the settings before the first fetch, or the report would load
  // once at 2 and again at the studio's number. `isLoading` (pending AND
  // fetching), not `isPending`: a query that never starts (the hook disables
  // itself for a system admin) stays pending forever, and a failed request
  // should fall back to the built-in number rather than spin.
  const lowCreditReady = lowCreditOverride !== null || !settingsLoading;
  const [range, setRange] = useState<AnalyticsRange>("1m");
  // Only meaningful for "expired" (a lookback window) — the other views are
  // forward-looking thresholds and never read this.
  const [customRange, setCustomRange] = useState<CustomDayRange>(null);

  // One payload serves both expiry views; low credits is its own query,
  // disabled on membership so no stray paket request fires.
  const { data: expiryData, isLoading: expiryLoading } =
    getPackageAnalyticsExpired(type, threshold, range, customRange, expiryReady);
  const { data: lowData, isLoading: lowLoading } = getPackageAnalyticsLowCredits(
    lowCreditThreshold,
    type === "paket" && lowCreditReady,
  );

  const exportSlug =
    view === "low_credits" ? "kredit-akan-habis" : EXPIRY_SLUGS[type][view];
  const exportParams =
    view === "expired"
      ? customRange
        ? { from_date: customRange.from, to_date: customRange.to }
        : { range }
      : view === "expiring_soon"
        ? { expiring_soon_days: threshold }
        : view === "unactivated"
          ? {}
          : { low_credit_threshold: lowCreditThreshold };

  // "Expired" rows sit under the customer's "Tidak Aktif" sub-tab, while
  // expiring-soon and low-credit rows are still valid/active packages, so
  // they sit under "Aktif" — matching whichever sub-tab holds the row clicked.
  const tabKey = type === "paket" ? "2" : "4";
  const subKey = view === "expired" ? "expired" : "active";
  const goToCustomer = (row: PackageExpiryRow) =>
    navigate(`${PATHS.CUSTOMER}/${row.customer_id}?tab=${tabKey}&sub=${subKey}`);

  // Each hook plans only when its view is the one on screen; the other
  // receives no data and does nothing.
  const lowCount = useReportPageCount(lowCreditsSpec, view === "low_credits" ? lowData : undefined, sortState);
  const expiryPdfSpec = useMemo(
    () => expirySpec(type, (view === "low_credits" ? "expiring_soon" : view) as ExpiryWhich),
    [type, view],
  );
  const expiryCount = useReportPageCount(expiryPdfSpec, view === "low_credits" ? undefined : expiryData, sortState);

  const pdfProps =
    view === "low_credits"
      ? lowData && {
          rowChoice: {
            pagesFor: lowCount.pagesFor,
            prepare: lowCount.prepare,
            available: Math.min(lowData.table.length, MAX_PDF_ROWS),
            total: lowData.table.length,
          },
          fileName: `spott-kredit-akan-habis-${creditThresholdStamp(lowCreditThreshold)}.pdf`,
          render: async (maxRows?: number) => {
            const { renderReportPdf } = await loadReportPdf();
            return renderReportPdf({
              slug: "kredit-akan-habis",
              studio: authState.business?.name ?? "",
              data: lowData,
              sortState,
              lowCreditThreshold,
              maxRows,
            });
          },
        }
      : expiryData && {
          // Each expiry view reads its own list, so the row choice follows
          // whichever one is on screen.
          rowChoice: {
            pagesFor: expiryCount.pagesFor,
            prepare: expiryCount.prepare,
            available: Math.min(expiryData[view].length, MAX_PDF_ROWS),
            total: expiryData[view].length,
          },
          fileName: `spott-${EXPIRY_SLUGS[type][view]}-${
            view === "expired"
              ? analyticsRangeStamp(range, customRange)
              : view === "unactivated"
                ? tzDate().format("YYYYMMDD")
                : thresholdStamp(threshold)
          }.pdf`,
          render: async (maxRows?: number) => {
            const { renderReportPdf } = await loadReportPdf();
            return renderReportPdf({
              slug: EXPIRY_SLUGS[type][view],
              studio: authState.business?.name ?? "",
              data: expiryData,
              sortState,
              expiringSoonDays: threshold,
              maxRows,
            });
          },
        };

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          <ExportMenu reportSlug={exportSlug} params={exportParams} pdf={pdfProps} />
        }
      />
      <Flex vertical gap={20}>
        <ConfigProvider theme={pillSegmentedTheme}>
          <Segmented
            // Sized to its labels and left-aligned (Andrew's call), like the
            // other Analitik toggles; a vertical Flex would otherwise stretch
            // it full width. The className carries the phone-width fallback
            // (full width, shrinking segments) — see index.css.
            className="status-view-segmented"
            style={{ alignSelf: "flex-start", maxWidth: "100%" }}
            value={view}
            onChange={(value) => handleViewChange(value as string)}
            options={views.map((v) => ({
              value: v,
              label: translate(`analyticsPage.package.view_${v}`),
            }))}
          />
        </ConfigProvider>
        {view === "expired" && (
          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
              {expiryData?.range_label}
            </span>
            <Flex align="center" gap={10} wrap="wrap">
              <CustomRangePicker value={customRange} onChange={setCustomRange} />
              <RangeSegmented
                value={range}
                active={!customRange}
                includeYtd={false}
                onChange={(value) => {
                  setCustomRange(null);
                  setRange(value);
                }}
              />
            </Flex>
          </Flex>
        )}
        {view === "expiring_soon" && (
          <Card styles={{ body: { padding: 22 } }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(17), color: colors.ink }}>
              {translate("analyticsPage.package.expiring_soon_rule_heading")}
            </div>
            <Flex align="center" gap={10} wrap="wrap" style={{ marginTop: 12 }}>
              <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
                {translate(`analyticsPage.${namespace}.expiring_soon_rule_prefix`)}
              </span>
              <InlineDaysInput
                value={threshold}
                onChange={setThreshold}
                min={EXPIRING_SOON_DAYS_MIN}
                max={EXPIRING_SOON_DAYS_MAX}
              />
              <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
                {translate("analyticsPage.package.expiring_soon_rule_suffix")}
              </span>
            </Flex>
          </Card>
        )}
        {view === "unactivated" && (
          <Card styles={{ body: { padding: 22 } }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(17), color: colors.ink }}>
              {translate(`analyticsPage.${namespace}.unactivated_rule_heading`)}
            </div>
            <div style={{ marginTop: 8, fontSize: textPx(14), color: colors.ink2 }}>
              {translate(`analyticsPage.${namespace}.unactivated_rule_desc`)}
            </div>
          </Card>
        )}
        {view === "low_credits" && (
          <Card styles={{ body: { padding: 22 } }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(17), color: colors.ink }}>
              {translate("analyticsPage.package.low_credits_rule_heading")}
            </div>
            <Flex align="center" gap={10} wrap="wrap" style={{ marginTop: 12 }}>
              <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
                {translate("analyticsPage.package.low_credits_rule_prefix")}
              </span>
              <InlineDaysInput
                value={lowCreditThreshold}
                onChange={setLowCreditThreshold}
                min={LOW_CREDIT_MIN}
                max={LOW_CREDIT_MAX}
              />
              <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
                {translate("analyticsPage.package.low_credits_rule_suffix")}
              </span>
            </Flex>
          </Card>
        )}
        <PackageExpiryTable
          // Remount per view: isolates each view's page-memory slot, resets
          // the visual sorter, and re-applies low_credits' default sort.
          key={view}
          onSortChange={onSortChange}
          emphasis={
            view === "low_credits" ? "credits" : view === "unactivated" ? "unactivated" : "expiry"
          }
          productType={type}
          showRemainingCredits={type === "paket"}
          rows={
            view === "low_credits" ? (lowData?.table ?? []) : (expiryData?.[view] ?? [])
          }
          loading={
            view === "low_credits" ? lowLoading || !lowCreditReady : expiryLoading || !expiryReady
          }
          onRowClick={goToCustomer}
          pageStorageKey={
            view === "low_credits"
              ? "analyticsLowCredits.paket"
              : `analyticsExpiry.${type}.${view}`
          }
        />
      </Flex>
    </>
  );
};

export default PackageExpiryReportContent;
